import React from 'react';
// Character limit based on database schema
const DOC_CHAR_LIMIT = 200;
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';
import type { LeaseEOI } from '../../../types/lease-eoi';

interface Props {
  initialData?: LeaseEOI;
  onSubmit: (data: Omit<LeaseEOI, 'eoi_id'>) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  onCancel: () => void;
  submitLabel: string;
  formId?: string;
}

export default function LeaseEOIForm({
  initialData,
  onSubmit,
  isSubmitting,
  error,
  onCancel,
  submitLabel,
  formId = 'lease-eoi-form'
}: Props) {
  const [formData, setFormData] = React.useState({
    eoi_lot: initialData?.eoi_lot || '',
    eoi_date: initialData?.eoi_date ? new Date(initialData.eoi_date).toISOString().split('T')[0] : '',
    eoi_doc: initialData?.eoi_doc || '',
    eoi_close_date: initialData?.eoi_close_date ? new Date(initialData.eoi_close_date).toISOString().split('T')[0] : ''
  });
  
  // Track character count
  const docCharCount = formData.eoi_doc.length;
  
  const [formError, setFormError] = React.useState<string | null>(null);

  // Fetch land lots for the dropdown
  const { data: landLots, isLoading: isLoadingLots } = useQuery({
    queryKey: ['land-lots'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('land_lots')
        .select('lot_id')
        .order('lot_id', { ascending: true });

      if (error) throw error;
      return data;
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    // Basic validation
    if (!formData.eoi_lot) {
      setFormError('Please select a lot');
      return;
    }
    
    if (!formData.eoi_date) {
      setFormError('Please enter an EOI date');
      return;
    }
    
    if (!formData.eoi_doc) {
      setFormError('Please enter document reference information');
      return;
    }
    
    if (!formData.eoi_close_date) {
      setFormError('Please enter a close date');
      return;
    }
    
    await onSubmit(formData);
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="eoi_lot" className="block text-sm font-medium text-gray-700">
          Lot
        </label>
        <select
          id="eoi_lot"
          name="eoi_lot"
          value={formData.eoi_lot}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
          required
        >
          <option value="">Select a lot</option>
          {isLoadingLots ? (
            <option value="" disabled>Loading...</option>
          ) : (
            landLots?.map((lot) => (
              <option key={lot.lot_id} value={lot.lot_id}>
                {lot.lot_id}
              </option>
            ))
          )}
        </select>
      </div>

      <div>
        <label htmlFor="eoi_date" className="block text-sm font-medium text-gray-700">
          EOI Date
        </label>
        <input
          type="date"
          id="eoi_date"
          name="eoi_date"
          value={formData.eoi_date}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          required
        />
      </div>

      <div>
        <label htmlFor="eoi_doc" className="block text-sm font-medium text-gray-700">
          Document Reference
        </label>
        <input
          id="eoi_doc"
          name="eoi_doc"
          value={formData.eoi_doc}
          onChange={handleChange}
          placeholder="Enter document reference"
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
          maxLength={DOC_CHAR_LIMIT}
          required
        />
        <div className="flex justify-between">
          <div className="mt-1">
            <p className="text-xs text-gray-500">
              Enter detailed information about the document (optional, max {DOC_CHAR_LIMIT} characters)
            </p>
            <p className="text-xs text-gray-500 italic">
              Add URL to the document repository as needed
            </p>
          </div>
          <p className={`mt-1 text-xs ${docCharCount >= DOC_CHAR_LIMIT * 0.9 ? 'text-amber-600' : 'text-gray-500'}`}>
            {docCharCount}/{DOC_CHAR_LIMIT}
          </p>
        </div>
      </div>

      <div>
        <label htmlFor="eoi_close_date" className="block text-sm font-medium text-gray-700">
          Close Date
        </label>
        <input
          type="date"
          id="eoi_close_date"
          name="eoi_close_date"
          value={formData.eoi_close_date}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          required
        />
      </div>

      {(error || formError) && (
        <div className="text-red-500 text-sm">{formError || error}</div>
      )}

      {/* Buttons are now in the modal footer */}
    </form>
  );
}
